import { shouldDisplayBorder } from '../../utils/elementBorder';
import { BACKGROUND_TEXT_MODE } from '../../constants';
import { getElementMask } from '../../masks';
import getTransformFlip from './getTransformFlip';
import {
  getTextBoxBackdropStyles,
  normalizeTextBoxBackdropValue,
} from '../text/getTextBoxBackdropStyles';

export function supportsElementBackdrop(element) {
  return (
    (element.type === 'image' || element.type === 'shape') &&
    !element.isBackground &&
    !element.isDefaultBackground
  );
}

export function hasElementBackdrop(element) {
  return (
    !element.isHidden &&
    supportsElementBackdrop(element) &&
    normalizeTextBoxBackdropValue(element.backdropBlur) > 0
  );
}

// Work in design pixels so editor zoom and output conversion happen once.
export function getBackdropMaskImage(element, mask, softness, feather = true) {
  const { width, height } = element;
  const expansion = softness * 2;
  const maskWidth = width + expansion * 2;
  const maskHeight = height + expansion * 2;
  const path = String(mask.path)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
  const shape = `<path d="${path}" fill="black" transform="translate(${expansion} ${expansion}) scale(${width} ${
    height * (mask.ratio || 1)
  })"/>`;
  const svg = [
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${maskWidth} ${maskHeight}" preserveAspectRatio="none">`,
    ...(softness > 0 && feather
      ? [
          '<defs>',
          `<filter id="soften" filterUnits="userSpaceOnUse" x="0" y="0" width="${maskWidth}" height="${maskHeight}">`,
          `<feGaussianBlur stdDeviation="${softness / 2}"/>`,
          '</filter></defs>',
          `<g filter="url(#soften)">${shape}</g>`,
        ]
      : [shape]),
    '</svg>',
  ].join('');
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}

export function getBackdropCornerRadii(element) {
  const { width, height, borderRadius: savedBorderRadius } = element;
  const borderRadius = savedBorderRadius || {};
  const values = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].map(
    (key) => normalizeTextBoxBackdropValue(borderRadius[key])
  );
  const [tl, tr, br, bl] = values;
  const factor = Math.min(
    1,
    ...[
      [width, tl + tr],
      [width, bl + br],
      [height, tl + bl],
      [height, tr + br],
    ]
      .filter(([, sum]) => sum > 0)
      .map(([side, sum]) => side / sum)
  );
  return values.map((value) => value * factor);
}

// Match the inner edge of the existing percentage-radius border box.
function getBackdropCornerGeometry(element, includeBorder = true) {
  const radii = getBackdropCornerRadii(element);
  const border =
    includeBorder && shouldDisplayBorder(element) ? element.border : null;
  const { width, height } = element;
  const left = border?.left || 0;
  const right = border?.right || 0;
  const top = border?.top || 0;
  const bottom = border?.bottom || 0;
  return {
    horizontal: radii.map((radius, index) =>
      Math.max(
        0,
        (radius * (width + left + right)) / (width || 1) -
          (index === 0 || index === 3 ? left : right)
      )
    ),
    vertical: radii.map((radius, index) =>
      Math.max(
        0,
        (radius * (height + top + bottom)) / (height || 1) -
          (index < 2 ? top : bottom)
      )
    ),
  };
}

function getRoundedBackdropMask(element, corners) {
  const { width: w, height: h } = element;
  const [tlx, trx, brx, blx] = corners.horizontal;
  const [tly, try_, bry, bly] = corners.vertical;
  const arc = (rx, ry, x, y) =>
    rx > 0 && ry > 0
      ? `A ${rx / w} ${ry / h} 0 0 1 ${x / w} ${y / h}`
      : `L ${x / w} ${y / h}`;
  return {
    ratio: 1,
    path: [
      `M ${tlx / w} 0 H ${(w - trx) / w}`,
      arc(trx, try_, w, try_),
      `V ${(h - bry) / h}`,
      arc(brx, bry, w - brx, h),
      `H ${blx / w}`,
      arc(blx, bly, 0, h - bly),
      `V ${tly / h}`,
      arc(tlx, tly, tlx, 0),
      'Z',
    ].join(' '),
  };
}

export function getElementBackdropStyles({
  element,
  applyFlip = true,
  includeBorder = true,
  offsetForBorderFlip = false,
  softFrame = false,
  ...units
}) {
  if (
    element.isHidden ||
    !supportsElementBackdrop(element) ||
    normalizeTextBoxBackdropValue(element.backdropBlur) === 0
  ) {
    return null;
  }
  const style = getTextBoxBackdropStyles({
    ...units,
    element: {
      ...element,
      backgroundTextMode: BACKGROUND_TEXT_MODE.NONE,
      textBoxBackdropBlur: element.backdropBlur,
      textBoxBackdropEdgeSoftness: element.backdropEdgeSoftness,
    },
  });
  const mask = getElementMask(element);
  const softness = normalizeTextBoxBackdropValue(element.backdropEdgeSoftness);
  const x = units.dataToStyleX || ((n) => `${n}px`);
  const y = units.dataToStyleY || ((n) => `${n}px`);
  if (softFrame && softness > 0) {
    // While the live blur is still rendering, the element's paint fades over
    // this same feathered mask (see SoftFrame). The mask image spans the
    // backdrop box: the element plus twice the softness on every side.
    // Editor only: output markup has no loading state.
    style['--combined-soft-mask-size'] = `calc(100% + ${x(
      softness * 4
    )}) calc(100% + ${y(softness * 4)})`;
  }
  // Resolve softness from the computed blur length. Output positions use
  // percentages, so parsing an X length as pixels would shrink export feather.
  style['--backdrop-feather-ratio'] =
    softness / (2 * normalizeTextBoxBackdropValue(element.backdropBlur));
  if (!mask || mask.type === 'rectangle') {
    const corners = getBackdropCornerGeometry(element, includeBorder);
    if (softness === 0) {
      style.borderRadius = `${corners.horizontal
        .map(x)
        .join(' ')} / ${corners.vertical.map(y).join(' ')}`;
    } else {
      const frame = getRoundedBackdropMask(element, corners);
      const maskImage = getBackdropMaskImage(element, frame, softness);
      Object.assign(style, {
        maskImage,
        WebkitMaskImage: maskImage,
        '--backdrop-frame-mask': getBackdropMaskImage(
          element,
          frame,
          softness,
          false
        ),
      });
    }
  }
  if (mask?.path && mask.type !== 'rectangle') {
    const softness = normalizeTextBoxBackdropValue(
      element.backdropEdgeSoftness
    );
    const maskImage = getBackdropMaskImage(element, mask, softness);
    delete style.borderRadius;
    delete style.overflow;
    Object.assign(style, {
      maskImage,
      WebkitMaskImage: maskImage,
      '--backdrop-frame-mask': getBackdropMaskImage(
        element,
        mask,
        softness,
        false
      ),
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat',
      maskSize: '100% 100%',
      WebkitMaskSize: '100% 100%',
    });
  }
  if (offsetForBorderFlip && applyFlip && shouldDisplayBorder(element)) {
    const { left, right, top, bottom } = element.border;
    const x = units.dataToStyleX || ((n) => `${n}px`);
    const y = units.dataToStyleY || ((n) => `${n}px`);
    style.left = x(
      (element.flip?.horizontal ? right - left : 0) - softness * 2
    );
    style.top = y((element.flip?.vertical ? bottom - top : 0) - softness * 2);
  }
  const flip = applyFlip && getTransformFlip(element.flip);
  return { ...style, ...(flip ? { transform: flip } : null) };
}
